import React from 'react';
import { Modal, Badge, Row, Col, Button } from 'react-bootstrap';
import { X, Calendar, User, Home, Phone, Users, Baby, Dog, FileText, Clock, Globe, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Reservation, ReservationHistory } from '../../services/types';
import { formatDate, calculateNights } from '../../utils/reservationHelpers';
import { useUserData } from '../../hooks';

interface ReservationDetailModalProps {
    show: boolean;
    onHide: () => void;
    reservation: Reservation | ReservationHistory | null;
    houseName?: string;
    onEdit?: () => void;
    onCancel?: () => void;
    onConfirm?: () => void;
    deleting?: boolean;
}

// Convert 24-hour time (HH:mm:ss) to 12-hour AM/PM format
const formatTimeTo12Hour = (time24: string | null | undefined) => {
    if (!time24) return null;
    const [hour24Str, minuteStr] = time24.split(':');
    const hour24 = parseInt(hour24Str, 10);
    const minute = parseInt(minuteStr, 10);
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
};

const ReservationDetailModal: React.FC<ReservationDetailModalProps> = ({
    show,
    onHide,
    reservation,
    houseName,
    onEdit,
    onCancel,
    onConfirm,
    deleting = false,
}) => {
    const { canEdit, canDelete } = useUserData();

    if (!reservation) return null;

    const nights = calculateNights(reservation.check_in_date, reservation.check_out_date);

    const getStatusConfig = (state: string) => {
        switch (state) {
            case 'confirmed':
                return { icon: CheckCircle2, color: '#10b981', bgColor: '#d1fae5', label: 'Confirmed' };
            case 'cancelled':
                return { icon: XCircle, color: '#ef4444', bgColor: '#fee2e2', label: 'Cancelled' };
            default:
                return { icon: AlertCircle, color: '#f59e0b', bgColor: '#fef3c7', label: 'Pending' };
        }
    };

    const statusConfig = getStatusConfig(reservation.reservation_state);
    const StatusIcon = statusConfig.icon;

    const isMobile = window.innerWidth < 768;

    return (
        <Modal show={show} onHide={onHide} size="xl" scrollable>
            {/* Header */}
            <Modal.Header className="border-bottom" style={{ padding: isMobile ? '1rem' : '1.5rem', backgroundColor: '#f8f9fa' }}>
                <div className="w-100">
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h4 className="mb-2 fw-bold" style={{ color: '#1f2937', fontSize: isMobile ? '1.1rem' : '1.5rem' }}>
                                <Calendar size={isMobile ? 20 : 28} className="me-2" style={{ marginTop: '-4px', color: '#3b82f6' }} />
                                Reservation Details
                            </h4>
                            {reservation.id_reservation_platform && (
                                <div className="mb-3" style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', color: '#6b7280' }}>
                                    Platform Reservation ID: <code style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', backgroundColor: '#e5e7eb', padding: '0.25rem 0.5rem', borderRadius: '4px', fontWeight: 600, color: '#374151' }}>
                                        {reservation.id_reservation_platform}
                                    </code>
                                </div>
                            )}
                            <Badge
                                style={{
                                    backgroundColor: statusConfig.bgColor,
                                    color: statusConfig.color,
                                    padding: isMobile ? '0.35rem 0.75rem' : '0.5rem 1rem',
                                    fontSize: isMobile ? '0.8rem' : '0.95rem',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    border: 'none'
                                }}
                            >
                                <StatusIcon size={14} className="me-2" />
                                {statusConfig.label}
                            </Badge>
                        </div>
                        <Button
                            variant="link"
                            className="text-secondary p-0"
                            onClick={onHide}
                            style={{ marginTop: '-8px' }}
                        >
                            <X size={isMobile ? 24 : 28} />
                        </Button>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body style={{ padding: isMobile ? '1rem' : '2rem' }}>
                <div style={{ color: '#374151' }}>
                    {/* Guest Information */}
                    <div className="mb-5">
                        <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                            <User size={14} className="me-2" style={{ marginTop: '-2px' }} />
                            Guest Information
                        </h6>

                        <Row className="g-4">
                            <Col xs={12} md={6}>
                                <div className="p-3" style={{ backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                    <div className="d-flex align-items-center mb-2">
                                        <User size={16} className="me-2" style={{ color: '#6b7280' }} />
                                        <span className="text-muted small">Guest Name</span>
                                    </div>
                                    <div className="fw-semibold" style={{ fontSize: '1.1rem', color: '#111827' }}>
                                        {reservation.name_first_guess || 'N/A'}
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12} md={6}>
                                <div
                                    className="p-3"
                                    style={{
                                        backgroundColor: '#f9fafb',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                        const phoneNumber = reservation.contact.replace(/\D/g, '');
                                        window.open(`https://wa.me/${phoneNumber}`, '_blank');
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#dcfce7';
                                        e.currentTarget.style.borderColor = '#86efac';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f9fafb';
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    <div className="d-flex align-items-center mb-2">
                                        <Phone size={16} className="me-2" style={{ color: '#6b7280' }} />
                                        <span className="text-muted small">Contact (Click to WhatsApp)</span>
                                    </div>
                                    <div className="fw-semibold" style={{ fontSize: '1.1rem', color: '#111827' }}>
                                        {reservation.contact}
                                    </div>
                                </div>
                            </Col>
                            {reservation.nationality && (
                                <Col xs={12} md={6}>
                                    <div className="p-3" style={{ backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                        <div className="d-flex align-items-center mb-2">
                                            <Globe size={16} className="me-2" style={{ color: '#6b7280' }} />
                                            <span className="text-muted small">Nationality</span>
                                        </div>
                                        <div className="fw-semibold" style={{ fontSize: '1.1rem', color: '#111827' }}>
                                            {reservation.nationality}
                                        </div>
                                    </div>
                                </Col>
                            )}
                            {reservation.platform && (
                                <Col xs={12} md={6}>
                                    <div className="p-3" style={{ backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                        <div className="d-flex align-items-center mb-2">
                                            <Home size={16} className="me-2" style={{ color: '#6b7280' }} />
                                            <span className="text-muted small">Booking Platform</span>
                                        </div>
                                        <div className="fw-semibold" style={{ fontSize: '1.1rem', color: '#111827' }}>
                                            {reservation.platform}
                                        </div>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>

                    <hr className="my-4" style={{ opacity: 0.1 }} />

                    {/* Stay Information */}
                    <div className="mb-5">
                        <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                            <Calendar size={14} className="me-2" style={{ marginTop: '-2px' }} />
                            Stay Details
                        </h6>

                        {houseName && (
                            <div className="mb-4 p-3" style={{ backgroundColor: '#e0f2fe', borderRadius: '8px', border: '1px solid #7dd3fc' }}>
                                <div className="d-flex align-items-center">
                                    <Home size={20} className="me-3" style={{ color: '#0284c7' }} />
                                    <span className="fw-semibold" style={{ fontSize: '1.1rem', color: '#0c4a6e' }}>{houseName}</span>
                                </div>
                            </div>
                        )}

                        <Row className="g-4">
                            <Col xs={12} md={6}>
                                <div className="p-3" style={{ backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #6ee7b7' }}>
                                    <div className="text-muted small mb-2">Check-in Date</div>
                                    <div className="fw-bold" style={{ fontSize: '1.15rem', color: '#047857' }}>
                                        {formatDate(reservation.check_in_date, 'MMM dd, yyyy')}
                                    </div>
                                    {reservation.check_in_hour && (
                                        <div className="d-flex align-items-center mt-2 pt-2" style={{ borderTop: '1px solid #a7f3d0' }}>
                                            <Clock size={16} className="me-2" style={{ color: '#059669' }} />
                                            <span style={{ color: '#047857', fontWeight: 600, fontSize: '0.95rem' }}>
                                                {formatTimeTo12Hour(reservation.check_in_hour)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col xs={12} md={6}>
                                <div className="p-3" style={{ backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
                                    <div className="text-muted small mb-2">Check-out Date</div>
                                    <div className="fw-bold" style={{ fontSize: '1.15rem', color: '#b45309' }}>
                                        {formatDate(reservation.check_out_date, 'MMM dd, yyyy')}
                                    </div>
                                    {reservation.check_out_hour && (
                                        <div className="d-flex align-items-center mt-2 pt-2" style={{ borderTop: '1px solid #fde68a' }}>
                                            <Clock size={16} className="me-2" style={{ color: '#d97706' }} />
                                            <span style={{ color: '#b45309', fontWeight: 600, fontSize: '0.95rem' }}>
                                                {formatTimeTo12Hour(reservation.check_out_hour)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col xs={12}>
                                <div className="p-3 text-center" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Duration</div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e40af' }}>
                                        {nights} <span style={{ fontSize: '1rem', fontWeight: 500 }}>night{nights !== 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <hr className="my-4" style={{ opacity: 0.1 }} />

                    {/* Additional Details */}
                    <div className="mb-5">
                        <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                            <Users size={14} className="me-2" style={{ marginTop: '-2px' }} />
                            Accommodation Details
                        </h6>

                        <Row className="g-3">
                            <Col xs={6} sm={3}>
                                <div className="text-center p-3" style={{ backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
                                    <div className="fw-bold" style={{ fontSize: '2rem', color: '#92400e' }}>{reservation.rooms}</div>
                                    <div className="text-muted small" style={{ fontWeight: 500 }}>Room{reservation.rooms !== 1 ? 's' : ''}</div>
                                </div>
                            </Col>
                            {reservation.number_of_guests && (
                                <Col xs={6} sm={3}>
                                    <div className="text-center p-3" style={{ backgroundColor: '#dbeafe', borderRadius: '8px', border: '1px solid #93c5fd' }}>
                                        <div className="fw-bold" style={{ fontSize: '2rem', color: '#1e40af' }}>{reservation.number_of_guests}</div>
                                        <div className="text-muted small" style={{ fontWeight: 500 }}>Guest{reservation.number_of_guests !== 1 ? 's' : ''}</div>
                                    </div>
                                </Col>
                            )}
                            {reservation.baby && (
                                <Col xs={6} sm={3}>
                                    <div className="text-center p-3" style={{ backgroundColor: '#fce7f3', borderRadius: '8px', border: '1px solid #fbcfe8' }}>
                                        <Baby size={28} className="mb-2" style={{ color: '#db2777' }} />
                                        <div className="text-muted small" style={{ fontWeight: 500 }}>Baby</div>
                                    </div>
                                </Col>
                            )}
                            {reservation.pets && (
                                <Col xs={6} sm={3}>
                                    <div className="text-center p-3" style={{ backgroundColor: '#e0e7ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
                                        <Dog size={28} className="mb-2" style={{ color: '#6366f1' }} />
                                        <div className="text-muted small" style={{ fontWeight: 500 }}>Pets</div>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>

                    {/* Observations */}
                    {reservation.observations && (
                        <div className="mb-3">
                            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                                <FileText size={14} className="me-2" style={{ marginTop: '-2px' }} />
                                Additional Notes
                            </h6>
                            <div className="p-4" style={{ backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', borderLeft: '4px solid #f59e0b' }}>
                                <p className="mb-0" style={{ fontSize: '1rem', lineHeight: '1.7', color: '#78350f', whiteSpace: 'pre-wrap' }}>
                                    {reservation.observations}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>

            {/* Footer */}
            <Modal.Footer className="bg-light border-top" style={{ padding: isMobile ? '1rem' : '1.25rem' }}>
                <div className={`d-flex ${isMobile ? 'flex-column' : 'gap-3'} w-100 ${isMobile ? '' : 'justify-content-end'}`} style={{ gap: isMobile ? '0.75rem' : undefined }}>
                    {onCancel && canDelete && reservation.reservation_state !== 'cancelled' && (
                        <Button
                            variant="outline-danger"
                            onClick={onCancel}
                            disabled={deleting}
                            className={isMobile ? 'w-100' : ''}
                            style={{
                                borderRadius: '8px',
                                padding: isMobile ? '0.75rem 1rem' : '0.6rem 1.5rem',
                                fontWeight: '600',
                                fontSize: isMobile ? '0.9rem' : '0.95rem',
                                borderWidth: '2px'
                            }}
                        >
                            {deleting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <XCircle size={18} className="me-2" style={{ marginTop: '-2px' }} />
                                    Delete Reservation
                                </>
                            )}
                        </Button>
                    )}
                    {onEdit && canEdit && (
                        <Button
                            variant="light"
                            onClick={onEdit}
                            className={isMobile ? 'w-100' : ''}
                            style={{
                                borderRadius: '8px',
                                padding: isMobile ? '0.75rem 1rem' : '0.6rem 1.5rem',
                                fontWeight: '600',
                                fontSize: isMobile ? '0.9rem' : '0.95rem',
                                border: '2px solid #dee2e6'
                            }}
                        >
                            Edit Details
                        </Button>
                    )}
                    {onConfirm && canEdit && reservation.reservation_state === 'pending' && (
                        <Button
                            variant="success"
                            onClick={onConfirm}
                            className={isMobile ? 'w-100' : ''}
                            style={{
                                borderRadius: '8px',
                                padding: isMobile ? '0.75rem 1rem' : '0.6rem 1.5rem',
                                fontWeight: '600',
                                fontSize: isMobile ? '0.9rem' : '0.95rem',
                                backgroundColor: '#10b981',
                                border: 'none'
                            }}
                        >
                            <CheckCircle2 size={18} className="me-2" style={{ marginTop: '-2px' }} />
                            Confirm Reservation
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ReservationDetailModal;